import {
	QuoteProductThreadEventKind,
	useOnQuoteProductThreadUpdatedSubscription,
	useSubscriptionRestored,
} from '@codezo/graphql';
import { buildSpecificationRows } from '@codezo/product-specs';
import {
	FileGrid,
	FileTile,
	fileMeta,
	RequestedProducts,
	SectionHead,
	type RequestedProduct,
} from '@codezo/quote-detail';
import { MessageSquare } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FilePreview, type FilePreviewItem } from '../../components/ui/file-preview';
import { orderedProductImages, ProductImage } from '../../components/ui/product-image';
import { Button } from '../../components/ui/button';
import { useQueryParam } from '../../hooks/useQueryParam';
import { formatDate } from '../../lib/utils';
import { JobThreadPanel } from './JobThreadPanel';
import type { VetterJob, VetterJobItem } from './prices/price-form';

type Viewer = { files: FilePreviewItem[]; index: number };

/**
 * What the supplier is asked to price, drawn with the same product card the
 * client and admin quote pages use — pictures, description, specifications
 * and the line's files, all opening in the platform's file viewer.
 *
 * Image and file urls are signed and short-lived: rendered straight from the
 * query, never stored.
 */
export function JobProducts({
	job,
	onCountsStale,
}: {
	job: VetterJob;
	/** Refetch the job — brings the server's counts back after a thread is read. */
	onCountsStale: () => void;
}) {
	/* Each product's message counts, kept live: they start from the job and
	   every product's thread is watched (see ThreadWatcher), so a new message
	   from the studio bumps its badge at once — for account and link sessions
	   alike. Subscriptions share the tab's one WebSocket, so watching each
	   product costs no extra connection. Reset whenever the job is refetched. */
	const [counts, setCounts] = useState<Record<string, { count: number; unread: number }>>({});
	useEffect(() => {
		setCounts(
			Object.fromEntries(
				job.items.map((i) => [
					i.orderQuoteItemId,
					{ count: i.commentThread.commentCount, unread: i.commentThread.unreadCount },
				])
			)
		);
	}, [job]);
	// A thread stream re-opened after a backend restart — counts may be behind.
	useSubscriptionRestored(onCountsStale, ['OnQuoteProductThreadUpdated']);
	const bump = (orderQuoteItemId: string, delta: { count: number; unread: number }) =>
		setCounts((prev) => {
			const cur = prev[orderQuoteItemId] ?? { count: 0, unread: 0 };
			return {
				...prev,
				[orderQuoteItemId]: {
					count: Math.max(0, cur.count + delta.count),
					unread: Math.max(0, cur.unread + delta.unread),
				},
			};
		});
	const [viewer, setViewer] = useState<Viewer | null>(null);
	/* The open thread lives in the URL (`?thread=<product>`), so a notification
	   link can land on it. Other params (the link's `token`) are kept. */
	const [threadItemId, setThreadItemId] = useQueryParam('thread', '');
	const openItem = job.items.find((i) => i.orderQuoteItemId === threadItemId);

	const items: RequestedProduct[] = job.items.map((item) => {
		const images = orderedProductImages(item.productImages);
		const rows = buildSpecificationRows({
			specifications: item.productSpecifications,
			dimensions: item.productDimensions,
			weight: item.productWeight,
		});

		return {
			id: item.orderQuoteItemId,
			product: {
				id: item.orderQuoteItemId,
				name: item.productName || 'Unnamed product',
				sku: item.productSku,
			},
			media: images.map((img, i) => (
				<ProductImage
					key={`${item.orderQuoteItemId}-${i}`}
					image={img}
					alt={item.productName ?? undefined}
					className="h-full w-full object-cover"
				/>
			)),
			onOpenImages: images.length
				? (index: number) =>
						setViewer({
							files: images.map((img, i) => ({
								id: `${item.orderQuoteItemId}-${i}`,
								url: img.url,
								name: item.productName ?? 'Product image',
								mimeType: 'image/jpeg',
							})),
							index,
						})
				: undefined,
			quantities: item.tiers.map((t) => t.quantity.toLocaleString('en-US')),
			deadline: item.deadline ? `Needed by ${formatDate(item.deadline)}` : null,
			description: item.productDescription,
			specs: rows.map((row) => [row.label, row.value] as const),
			primaryAction: (
				<MessagesButton
					count={counts[item.orderQuoteItemId]?.count ?? item.commentThread.commentCount}
					unread={counts[item.orderQuoteItemId]?.unread ?? item.commentThread.unreadCount}
					onClick={() => setThreadItemId(item.orderQuoteItemId)}
				/>
			),
			files: (
				<ItemFiles
					files={item.attachments}
					onOpen={(files, index) => setViewer({ files, index })}
				/>
			),
		};
	});

	return (
		<section>
			<SectionHead title="Products" meta="what you're asked to price" />
			<RequestedProducts items={items} />
			{viewer && (
				<FilePreview
					files={viewer.files}
					initialIndex={viewer.index}
					onClose={() => setViewer(null)}
				/>
			)}
			{/* The open product's panel watches its own thread; the rest are watched here. */}
			{job.items
				.filter((i) => i.orderQuoteItemId !== openItem?.orderQuoteItemId)
				.map((i) => (
					<ThreadWatcher
						key={i.orderQuoteItemId}
						orderQuoteItemId={i.orderQuoteItemId}
						onAdded={() => bump(i.orderQuoteItemId, { count: 1, unread: 1 })}
						onDeleted={() => bump(i.orderQuoteItemId, { count: -1, unread: 0 })}
					/>
				))}
			{openItem && (
				<JobThreadPanel
					open
					onClose={() => {
						setThreadItemId('');
						onCountsStale();
					}}
					orderQuoteItemId={openItem.orderQuoteItemId}
					productName={openItem.productName || 'Unnamed product'}
					onRead={() =>
						setCounts((prev) => ({
							...prev,
							[openItem.orderQuoteItemId]: {
								count: prev[openItem.orderQuoteItemId]?.count ?? 0,
								unread: 0,
							},
						}))
					}
				/>
			)}
		</section>
	);
}

/** The line's artwork and tech packs, as tiles that open in the viewer. */
function ItemFiles({
	files,
	onOpen,
}: {
	files: VetterJobItem['attachments'];
	onOpen: (files: FilePreviewItem[], index: number) => void;
}) {
	if (files.length === 0) {
		return <span className="text-sm text-text-4">No files on this product.</span>;
	}

	const previewFiles: FilePreviewItem[] = files.map((f) => ({
		id: f.id,
		url: f.url,
		name: f.name,
		mimeType: f.mimeType,
		size: f.size,
	}));

	return (
		<div className="flex flex-col gap-2">
			<span className="text-xs font-medium tracking-[0.14em] text-text-3 uppercase">
				Files
				<span className="ml-2 font-mono text-xs font-normal tracking-normal text-text-4 normal-case">
					{files.length}
				</span>
			</span>
			<FileGrid>
				{files.map((file, i) => (
					<FileTile
						key={file.id}
						name={file.name}
						meta={fileMeta(file.name, file.mimeType, file.size)}
						description={file.description}
						// Only a real image goes in an <img>; a PDF's url renders a broken glyph.
						preview={
							file.mimeType.startsWith('image/') ? (
								<img src={file.url} alt="" className="h-full w-full object-cover" />
							) : undefined
						}
						onOpen={() => onOpen(previewFiles, i)}
					/>
				))}
			</FileGrid>
		</div>
	);
}

/** Opens the product's thread; the count is what's unread, else how many messages. */
function MessagesButton({
	count,
	unread,
	onClick,
}: {
	count: number;
	unread: number;
	onClick: () => void;
}) {
	return (
		<Button variant="outline" size="sm" onClick={onClick}>
			<MessageSquare size={13} data-icon="inline-start" />
			Messages
			{unread > 0 ? (
				<span className="ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-2xs font-medium text-primary-foreground">
					{unread > 9 ? '9+' : unread}
				</span>
			) : (
				count > 0 && <span className="ml-0.5 text-xs text-text-4">{count}</span>
			)}
		</Button>
	);
}

/**
 * Listens to one product's thread while its panel is closed. Only someone
 * else's new message counts as unread; your own (from another tab) adds to the
 * total only. Renders nothing.
 */
function ThreadWatcher({
	orderQuoteItemId,
	onAdded,
	onDeleted,
}: {
	orderQuoteItemId: string;
	onAdded: () => void;
	onDeleted: () => void;
}) {
	useOnQuoteProductThreadUpdatedSubscription({
		variables: { input: { orderQuoteItemId } },
		onData: ({ data: event }) => {
			const update = event.data?.quoteProductThreadUpdated;
			if (!update) return;
			if (update.kind === QuoteProductThreadEventKind.Added && !update.comment.isMine)
				onAdded();
			if (update.kind === QuoteProductThreadEventKind.Deleted) onDeleted();
		},
	});
	return null;
}
